import { DependencyList, useCallback, useEffect, useState } from "react";

export default function useAsyncUpdate<T>(updateFunc: () => Promise<T>, deps?: DependencyList): [T | undefined, () => void] {
  const [value, setValue] = useState<T>();
  const update = useCallback(() => {
    updateFunc().then(setValue);
  }, deps ?? []);
  useEffect(update, [update]);

  return [value, update];
}

// 制約の引数型は never(あらゆる1引数の非同期関数を受けるための反変ボトム型)。
// 実際の引数型・戻り値型は Parameters / ReturnType で呼び出し側の関数から推論される。
// 実装内では型引数が不透明になるため、引数型・戻り値型へ揃える精密な型表明で橋渡しする。
export function useAsyncUpdateWithArgs<UpdateFunc extends (requestData: never) => Promise<unknown>>(updateFunc: UpdateFunc, defaultData?: Parameters<UpdateFunc>[0], deps?: DependencyList): [Awaited<ReturnType<UpdateFunc>> | undefined | null, (arg: Parameters<UpdateFunc>[0]) => void] {
  type Arg = Parameters<UpdateFunc>[0];
  type Result = Awaited<ReturnType<UpdateFunc>>;
  const [value, setValue] = useState<Result>();

  const update = useCallback(
    (requestData?: Arg) => {
      if (defaultData || requestData) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- never 制約のジェネリクスは実装内で不透明になるため、引数・戻り値型へ揃える精密表明で橋渡しする
        const callable = updateFunc as (requestData: Arg) => Promise<Result>;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上(defaultData/requestData の合成を Arg へ揃える)
        callable((requestData ?? defaultData) as Arg).then(setValue);
      }
    },
    deps ?? []
  );

  useEffect(update, [update]);

  return [value, update];
}